import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../../components/AuthContext';
import { 
  FaPlus, 
  FaMinus, 
  FaSearch, 
  FaTimes, 
  FaEye, 
  FaFilter, 
  FaDownload,
  FaBox,
  FaCalendarAlt,
  FaSave,
  FaHistory,
  FaChevronLeft,
  FaChevronRight,
  FaMapMarkerAlt,
  FaStickyNote,
  FaTrash,
  FaUndo,
  FaBoxes,
  FaCheck,
  FaBan,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaFileAlt,
  FaUser
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import Select from 'react-select';

const StockItemsDestroy = () => {
  const { user } = useContext(AuthContext);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const interval = setInterval(() => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stockItems, setStockItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [items, setItems] = useState([{
    stockItemId: null,
    stockItemName: '',
    stockItemDetails: null,
    destroyedDamagedQuantity: 0,
    reason: '',
    availableDamaged: 0
  }]);

  const [showStockItemsModal, setShowStockItemsModal] = useState(false);
  const [stockItemsSearch, setStockItemsSearch] = useState('');
  const [stockItemsPage, setStockItemsPage] = useState(1);
  const [stockItemsLimit] = useState(10);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  const [selectedStockItemIndex, setSelectedStockItemIndex] = useState(0);
  
  // History state
  const [destroyHistory, setDestroyHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit] = useState(10);
  const [totalHistory, setTotalHistory] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [historyFilters, setHistoryFilters] = useState({
    location: null,
    startDate: null,
    endDate: null,
    status: null
  });
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('entry');

  // Approval state
  const [approving, setApproving] = useState(false);

  // Refs
  const searchInputRef = useRef(null);
  const quantityRefs = useRef([]);

  // Initial data loading
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Load history when filters or pagination changes
  useEffect(() => {
    fetchDestroyHistory();
  }, [historyPage, historyFilters]);

  // Focus search when modal opens
  useEffect(() => {
    if (showStockItemsModal && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [showStockItemsModal]);

  // Update available stock when location changes
  useEffect(() => {
    if (selectedLocation) {
      const updatedItems = items.map(item => {
        if (item.stockItemId) {
          const damaged = getAvailableDamaged(item.stockItemId, selectedLocation.value);
          return { ...item, availableDamaged: damaged };
        }
        return item;
      });
      setItems(updatedItems);
    }
  }, [selectedLocation]);

  // Update refs when items change
  useEffect(() => {
    quantityRefs.current = quantityRefs.current.slice(0, items.length);
  }, [items.length]);

  // Filter and paginate stock items for modal
  const filteredStockItems = stockItems.filter(si => {
    const term = (stockItemsSearch || '').toLowerCase();
    return (
      si.name?.toLowerCase().includes(term) ||
      si.category?.name?.toLowerCase().includes(term) ||
      si.brand?.name?.toLowerCase().includes(term) ||
      si.unitOfMeasure?.toLowerCase().includes(term) ||
      si.id?.toLowerCase().includes(term)
    );
  });

  const paginatedStockItems = filteredStockItems.slice(
    (stockItemsPage - 1) * stockItemsLimit,
    stockItemsPage * stockItemsLimit
  );

  const totalStockItemsPages = Math.ceil(filteredStockItems.length / stockItemsLimit);

  useEffect(() => {
    setSelectedStockItemIndex(0);
  }, [stockItemsSearch, stockItemsPage]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const companyId = user?.user?.company?._id || user?.user?.company;
      const [stockItemsRes, locationsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/stock-items?userId=${user.user._id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/locations/companyId/${companyId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (stockItemsRes.ok) {
        const data = await stockItemsRes.json();
        setStockItems(Array.isArray(data) ? data : data.data || []);
      }

      if (locationsRes.ok) {
        const data = await locationsRes.json();
        setLocations(Array.isArray(data) ? data : data.data || []);
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load initial data' });
    } finally {
      setLoading(false);
    }
  };

  const fetchDestroyHistory = async () => {
    setHistoryLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: historyPage.toString(),
        limit: historyLimit.toString(),
        ...(historyFilters.location && { location: historyFilters.location }),
        ...(historyFilters.startDate && { startDate: historyFilters.startDate }),
        ...(historyFilters.endDate && { endDate: historyFilters.endDate }),
        ...(historyFilters.status && { status: historyFilters.status })
      });

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/stock-items-destroy?${queryParams}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();

      if (data.success) {
        setDestroyHistory(data.data?.destroyEntries || []);
        setTotalHistory(data.data?.pagination?.totalItems || 0);
        setTotalPages(data.data?.pagination?.totalPages || 0);
      } else {
        setDestroyHistory([]);
        setTotalHistory(0);
      }
    } catch (error) {
      console.error('Error fetching destroy history:', error);
      setDestroyHistory([]);
      setTotalHistory(0);
    } finally {
      setHistoryLoading(false);
    }
  };

  const getAvailableDamaged = (stockItemId, locationId) => {
    const stockItem = stockItems.find(s => s._id === stockItemId);
    if (!stockItem || !stockItem.stock) return 0;
    const locationStock = stockItem.stock.find(s => 
      (s.location._id || s.location) === locationId || s.location._id === locationId || s.location === locationId
    );
    return locationStock?.damagedQuantity || 0;
  };

  const handleStockItemSelect = (stockItem, index) => {
    if (!selectedLocation) {
      Swal.fire({ icon: 'warning', title: 'Select Location', text: 'Please select a location first' });
      return;
    }

    // Check duplicate
    const existingIndex = items.findIndex(item => item.stockItemId === stockItem._id);
    if (existingIndex !== -1 && existingIndex !== index) {
      Swal.fire({ icon: 'warning', title: 'Item Already Added', text: 'This stock item is already in the list' });
      return;
    }

    const damaged = getAvailableDamaged(stockItem._id, selectedLocation.value);
    
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      stockItemId: stockItem._id,
      stockItemName: stockItem.name,
      stockItemDetails: stockItem,
      availableDamaged: damaged
    };
    setItems(updatedItems);
    setShowStockItemsModal(false);
    setCurrentItemIndex(null);
    setStockItemsSearch('');
    setSelectedStockItemIndex(0);

    setTimeout(() => quantityRefs.current[index]?.focus(), 100);
  };

  const addItem = () => {
    setItems([...items, {
      stockItemId: null,
      stockItemName: '',
      stockItemDetails: null,
      destroyedDamagedQuantity: 0,
      reason: '',
      availableDamaged: 0
    }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    if (field === 'destroyedDamagedQuantity') {
      const numValue = Math.max(0, parseInt(value) || 0);
      if (numValue > updatedItems[index].availableDamaged) {
        Swal.fire({
          icon: 'warning',
          title: 'Insufficient Stock',
          text: `Available damaged quantity: ${updatedItems[index].availableDamaged}`
        });
        return;
      }
      updatedItems[index][field] = numValue;
    } else {
      updatedItems[index][field] = value;
    }
    setItems(updatedItems);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      if (index === items.length - 1) {
        addItem();
        setTimeout(() => quantityRefs.current[index + 1]?.focus(), 0);
      } else {
        quantityRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleModalKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowStockItemsModal(false);
      return;
    }
    const count = paginatedStockItems.length;
    if (count === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedStockItemIndex(prev => prev < count - 1 ? prev + 1 : prev);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedStockItemIndex(prev => prev > 0 ? prev - 1 : prev);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = paginatedStockItems[selectedStockItemIndex];
      if (selected && currentItemIndex !== null) {
        handleStockItemSelect(selected, currentItemIndex);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedLocation) {
      Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please select a location' });
      return;
    }

    if (!reason.trim()) {
      Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please provide a reason for destruction' });
      return;
    }

    const validItems = items.filter(item => item.stockItemId && item.destroyedDamagedQuantity > 0);

    if (validItems.length === 0) {
      Swal.fire({ icon: 'warning', title: 'No Valid Items', text: 'Please add at least one item with damaged quantity' });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        location: selectedLocation.value,
        destroyDate: selectedDate,
        reason,
        remarks,
        items: validItems.map(item => ({
          stockItemId: item.stockItemId,
          destroyedDamagedQuantity: item.destroyedDamagedQuantity,
          reason: item.reason
        }))
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/stock-items-destroy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        Swal.fire({ icon: 'success', title: 'Success', text: 'Destroy entry created (pending approval)' });
        
        // Reset form
        setItems([{
          stockItemId: null, stockItemName: '', stockItemDetails: null,
          destroyedDamagedQuantity: 0, reason: '', availableDamaged: 0
        }]);
        setReason('');
        setRemarks('');
        
        fetchDestroyHistory();
        setActiveTab('history');
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: data.message || 'Failed to create destroy entry' });
      }
    } catch (error) {
      console.error('Error creating destroy entry:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to create destroy entry' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproval = async (entryId, status) => {
    const action = status === 'approved' ? 'approve' : 'reject';
    
    const result = await Swal.fire({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Entry?`,
      text: status === 'approved' 
        ? 'This will deduct the damaged stock. This action cannot be undone.' 
        : 'This will reject the destroy entry.',
      icon: status === 'approved' ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonColor: status === 'approved' ? '#16a34a' : '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: `Yes, ${action} it`,
      input: 'textarea',
      inputLabel: 'Remarks (optional)',
      inputPlaceholder: 'Enter approval remarks...',
      inputAttributes: { 'aria-label': 'Approval remarks' }
    });

    if (!result.isConfirmed) return;

    setApproving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/stock-items-destroy/${entryId}/approval`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          status,
          approvalRemarks: result.value || ''
        })
      });

      const data = await response.json();

      if (data.success) {
        Swal.fire({ icon: 'success', title: 'Success', text: `Entry ${status} successfully` });
        fetchDestroyHistory();
        if (showDetailsModal) {
          setSelectedEntry(prev => prev ? { ...prev, status } : null);
          setShowDetailsModal(false);
        }
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: data.message || `Failed to ${action} entry` });
      }
    } catch (error) {
      console.error(`Error ${action}ing entry:`, error);
      Swal.fire({ icon: 'error', title: 'Error', text: `Failed to ${action} entry` });
    } finally {
      setApproving(false);
    }
  };

  const handleViewDetails = async (entry) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/stock-items-destroy/${entry._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setSelectedEntry(data.data);
      } else {
        setSelectedEntry(entry);
      }
    } catch {
      setSelectedEntry(entry);
    }
    setShowDetailsModal(true);
  };

  const handleHistoryFilterChange = (field, value) => {
    setHistoryFilters(prev => ({ ...prev, [field]: value }));
    setHistoryPage(1);
  };

  const resetHistoryFilters = () => {
    setHistoryFilters({ location: null, startDate: null, endDate: null, status: null });
    setHistoryPage(1);
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: <FaClock className="mr-1" /> },
      approved: { bg: 'bg-green-100', text: 'text-green-800', icon: <FaCheckCircle className="mr-1" /> },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: <FaTimesCircle className="mr-1" /> }
    };
    const c = config[status] || config.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
        {c.icon} {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Destroy ID', 'Date', 'Location', 'Status', 'Stock Item Name', 'Stock Item ID', 'Damaged Qty Destroyed', 'Item Reason', 'Overall Reason', 'Created By', 'Remarks'],
      ...destroyHistory.flatMap(entry =>
        (entry.items || []).map(item => [
          entry.destroyId || 'N/A',
          formatDate(entry.destroyDate),
          entry.location?.name || entry.location?.locationName || 'N/A',
          entry.status || 'N/A',
          item.stockItemId?.name || 'N/A',
          item.stockItemId?.id || 'N/A',
          item.destroyedDamagedQuantity || 0,
          item.reason || '',
          entry.reason || '',
          entry.createdBy?.name || 'Unknown',
          entry.remarks || ''
        ])
      )
    ].map(row => row.map(val => `"${val}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock-items-destroy-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // React Select dark mode styles
  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '38px',
      fontSize: '14px',
      backgroundColor: darkMode ? '#374151' : base.backgroundColor,
      borderColor: darkMode ? '#4B5563' : base.borderColor,
      color: darkMode ? '#F3F4F6' : base.color,
      '&:hover': { borderColor: darkMode ? '#6B7280' : base.borderColor }
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: darkMode ? '#374151' : base.backgroundColor,
      border: darkMode ? '1px solid #4B5563' : base.border
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused
        ? (darkMode ? '#4B5563' : '#EBF5FF')
        : state.isSelected
          ? (darkMode ? '#1F2937' : '#2563EB')
          : (darkMode ? '#374151' : base.backgroundColor),
      color: darkMode ? '#F3F4F6' : base.color,
      '&:active': { backgroundColor: darkMode ? '#1F2937' : base.backgroundColor }
    }),
    singleValue: (base) => ({ ...base, color: darkMode ? '#F3F4F6' : base.color }),
    input: (base) => ({ ...base, color: darkMode ? '#F3F4F6' : base.color }),
    placeholder: (base) => ({ ...base, color: darkMode ? '#9CA3AF' : base.color })
  };

  return (
    <div className={`min-h-screen p-6 transition-colors duration-300 ${darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gray-50'}`}>
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
            <FaTrash className="inline mr-2 text-red-600" />
            Stock Items Destroy
          </h1>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
            Write off damaged stock items with approval workflow
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className={`flex space-x-8 ${darkMode ? 'border-b border-gray-700' : ''}`}>
            <button
              onClick={() => setActiveTab('entry')}
              className={`${
                activeTab === 'entry'
                  ? 'border-red-500 text-red-600'
                  : `border-transparent ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:border-gray-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <FaPlus className="mr-2" />
              Create Entry
            </button>
            <button
              onClick={() => { setActiveTab('history'); fetchDestroyHistory(); }}
              className={`${
                activeTab === 'history'
                  ? 'border-red-500 text-red-600'
                  : `border-transparent ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:border-gray-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <FaHistory className="mr-2" />
              History ({totalHistory})
            </button>
          </nav>
        </div>

        {/* Entry Form Tab */}
        {activeTab === 'entry' && (
          <div className={`rounded-lg shadow-md p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Header Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaMapMarkerAlt className="inline mr-1" />
                    Location *
                  </label>
                  <Select
                    value={selectedLocation}
                    onChange={setSelectedLocation}
                    options={locations.map(loc => ({
                      value: loc._id,
                      label: loc.name || loc.locationName
                    }))}
                    placeholder="Select location"
                    isClearable
                    className="text-sm"
                    styles={selectStyles}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaCalendarAlt className="inline mr-1" />
                    Date *
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'}`}
                    required
                  />
                </div>
              </div>

              {/* Overall Reason */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaExclamationTriangle className="inline mr-1 text-yellow-500" />
                  Reason for Destruction *
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'}`}
                  placeholder="e.g., Water damage, Fire damage, Expired items, Beyond repair..."
                  required
                />
              </div>

              {/* Items Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto border-collapse">
                  <thead>
                    <tr className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        Stock Item
                      </th>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        <FaBoxes className="inline mr-1" />
                        Damaged Qty to Destroy
                      </th>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        Available Damaged
                      </th>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        Item Reason
                      </th>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <div
                            className={`flex items-center justify-between cursor-pointer p-2 rounded border border-dashed ${darkMode ? 'border-gray-500 hover:bg-gray-600' : 'border-gray-300 hover:bg-blue-50'}`}
                            onClick={() => {
                              setCurrentItemIndex(index);
                              setShowStockItemsModal(true);
                            }}
                          >
                            <span className={`text-sm ${darkMode ? 'text-gray-200' : ''}`}>
                              {item.stockItemName || 'Click to select stock item'}
                            </span>
                            <FaSearch className={darkMode ? 'text-gray-500' : 'text-gray-400'} />
                          </div>
                          {item.stockItemDetails && (
                            <div className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              ID: {item.stockItemDetails.id} | 
                              Unit: {item.stockItemDetails.unitOfMeasure} |
                              Brand: {item.stockItemDetails.brand?.name}
                            </div>
                          )}
                        </td>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <input
                            ref={el => quantityRefs.current[index] = el}
                            type="number"
                            value={item.destroyedDamagedQuantity}
                            onChange={(e) => handleItemChange(index, 'destroyedDamagedQuantity', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                            className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-red-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'}`}
                            min="0"
                            max={item.availableDamaged}
                          />
                        </td>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <div className="text-red-600 text-sm font-medium">
                            {item.availableDamaged}
                          </div>
                        </td>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <input
                            type="text"
                            value={item.reason}
                            onChange={(e) => handleItemChange(index, 'reason', e.target.value)}
                            className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-red-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'}`}
                            placeholder="Specific reason..."
                          />
                        </td>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                            disabled={items.length === 1}
                          >
                            <FaMinus size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Add Item Button */}
              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={addItem}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <FaPlus className="mr-2" />
                  Add Item
                </button>
              </div>

              {/* Remarks */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaStickyNote className="inline mr-1" />
                  Remarks
                </label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'}`}
                  rows="3"
                  placeholder="Enter any additional remarks..."
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <FaSave className="mr-2" />
                      Create Destroy Entry
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className={`rounded-lg shadow-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* History Header */}
            <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex justify-between items-center mb-4">
                <h2 className={`text-xl font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Destroy History</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={exportToCSV}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <FaDownload className="mr-2" />
                    Export CSV
                  </button>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                  >
                    <FaFilter className="mr-2" />
                    Filters
                  </button>
                </div>
              </div>

              {/* Filters */}
              {showFilters && (
                <div className={`grid grid-cols-1 md:grid-cols-5 gap-4 p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Location</label>
                    <Select
                      value={locations.find(l => l._id === historyFilters.location) ? {
                        value: historyFilters.location,
                        label: locations.find(l => l._id === historyFilters.location)?.name || locations.find(l => l._id === historyFilters.location)?.locationName
                      } : null}
                      onChange={(opt) => handleHistoryFilterChange('location', opt?.value || null)}
                      options={locations.map(loc => ({ value: loc._id, label: loc.name || loc.locationName }))}
                      placeholder="All locations"
                      isClearable
                      className="text-sm"
                      styles={selectStyles}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</label>
                    <Select
                      value={historyFilters.status ? { value: historyFilters.status, label: historyFilters.status.charAt(0).toUpperCase() + historyFilters.status.slice(1) } : null}
                      onChange={(opt) => handleHistoryFilterChange('status', opt?.value || null)}
                      options={[
                        { value: 'pending', label: 'Pending' },
                        { value: 'approved', label: 'Approved' },
                        { value: 'rejected', label: 'Rejected' }
                      ]}
                      placeholder="All statuses"
                      isClearable
                      className="text-sm"
                      styles={selectStyles}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Start Date</label>
                    <input
                      type="date"
                      value={historyFilters.startDate || ''}
                      onChange={(e) => handleHistoryFilterChange('startDate', e.target.value)}
                      className={`w-full px-2 py-1 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-200' : 'border-gray-300'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>End Date</label>
                    <input
                      type="date"
                      value={historyFilters.endDate || ''}
                      onChange={(e) => handleHistoryFilterChange('endDate', e.target.value)}
                      className={`w-full px-2 py-1 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-200' : 'border-gray-300'}`}
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={resetHistoryFilters}
                      className="flex items-center px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                    >
                      <FaUndo className="mr-1" />
                      Reset
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* History Table */}
            <div className="overflow-x-auto">
              {historyLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                  <span className={`ml-2 ${darkMode ? 'text-gray-300' : ''}`}>Loading...</span>
                </div>
              ) : destroyHistory.length === 0 ? (
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No destroy entries found
                </div>
              ) : (
                <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Destroy ID
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Date
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Location
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Items
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Total Damaged Qty
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Status
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Created By
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                    {destroyHistory.map((entry) => (
                      <tr key={entry._id} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {entry.destroyId}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {formatDate(entry.destroyDate)}
                        </td>
                        <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">
                            {entry.location?.name || entry.location?.locationName || 'N/A'}
                          </span>
                        </td>
                        <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {entry.items?.length || 0} items
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {entry.items?.reduce((sum, item) => sum + (item.destroyedDamagedQuantity || 0), 0) || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(entry.status)}
                        </td>
                        <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {entry.createdBy?.name || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewDetails(entry)}
                              className="text-blue-600 hover:text-blue-900 flex items-center"
                            >
                              <FaEye className="mr-1" /> View
                            </button>
                            {entry.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleApproval(entry._id, 'approved')}
                                  disabled={approving}
                                  className="text-green-600 hover:text-green-900 flex items-center disabled:opacity-50"
                                >
                                  <FaCheck className="mr-1" /> Approve
                                </button>
                                <button
                                  onClick={() => handleApproval(entry._id, 'rejected')}
                                  disabled={approving}
                                  className="text-red-600 hover:text-red-900 flex items-center disabled:opacity-50"
                                >
                                  <FaBan className="mr-1" /> Reject
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={`px-6 py-3 flex items-center justify-between border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Showing{' '}
                      <span className="font-medium">{((historyPage - 1) * historyLimit) + 1}</span>
                      {' '}to{' '}
                      <span className="font-medium">{Math.min(historyPage * historyLimit, totalHistory)}</span>
                      {' '}of{' '}
                      <span className="font-medium">{totalHistory}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setHistoryPage(1)}
                        disabled={historyPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-400 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
                      >
                        <FaAngleDoubleLeft />
                      </button>
                      <button
                        onClick={() => setHistoryPage(Math.max(1, historyPage - 1))}
                        disabled={historyPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-400 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
                      >
                        <FaChevronLeft />
                      </button>
                      
                      {[...Array(Math.min(5, totalPages))].map((_, index) => {
                        const pageNumber = Math.max(1, Math.min(totalPages - 4, historyPage - 2)) + index;
                        if (pageNumber <= totalPages) {
                          return (
                            <button
                              key={pageNumber}
                              onClick={() => setHistoryPage(pageNumber)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                pageNumber === historyPage
                                  ? 'z-10 bg-red-50 border-red-500 text-red-600'
                                  : darkMode ? 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        }
                        return null;
                      })}
                      
                      <button
                        onClick={() => setHistoryPage(Math.min(totalPages, historyPage + 1))}
                        disabled={historyPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-400 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
                      >
                        <FaChevronRight />
                      </button>
                      <button
                        onClick={() => setHistoryPage(totalPages)}
                        disabled={historyPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-400 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
                      >
                        <FaAngleDoubleRight />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stock Items Selection Modal */}
        {showStockItemsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div 
              className={`rounded-lg shadow-xl w-11/12 md:w-3/4 lg:w-2/3 max-h-[90vh] flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
              onKeyDown={handleModalKeyDown}
              tabIndex={-1}
            >
              <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className={`text-lg font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Select Stock Item</h3>
                  <button
                    onClick={() => setShowStockItemsModal(false)}
                    className={darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}
                  >
                    <FaTimes size={20} />
                  </button>
                </div>
                <div className="relative">
                  <FaSearch className={`absolute left-3 top-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={stockItemsSearch}
                    onChange={(e) => { setStockItemsSearch(e.target.value); setStockItemsPage(1); }}
                    className={`w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400' : 'border-gray-300'}`}
                    placeholder="Search stock items..."
                  />
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6">
                {paginatedStockItems.length === 0 ? (
                  <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No stock items found
                  </div>
                ) : (
                  <div className="space-y-2">
                    {paginatedStockItems.map((stockItem, index) => {
                      const damaged = selectedLocation ? getAvailableDamaged(stockItem._id, selectedLocation.value) : 0;
                      return (
                        <div
                          key={stockItem._id}
                          onClick={() => handleStockItemSelect(stockItem, currentItemIndex)}
                          className={`p-4 border rounded-lg cursor-pointer ${
                            index === selectedStockItemIndex 
                              ? (darkMode ? 'bg-red-900/30 border-red-500' : 'bg-red-100 border-red-500')
                              : (darkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-200 hover:bg-red-50')
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className={`font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{stockItem.name}</h4>
                              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                ID: {stockItem.id} | Unit: {stockItem.unitOfMeasure}
                              </div>
                              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                Brand: {stockItem.brand?.name} | Category: {stockItem.category?.name}
                              </div>
                              {selectedLocation && (
                                <div className={`text-xs mt-1 ${damaged > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                  {damaged > 0 
                                    ? `Damaged available: ${damaged}`
                                    : 'No damaged stock in selected location'}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {totalStockItemsPages > 1 && (
                <div className={`p-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex justify-between items-center">
                    <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Page {stockItemsPage} of {totalStockItemsPages} ({filteredStockItems.length} total items)
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setStockItemsPage(Math.max(1, stockItemsPage - 1))}
                        disabled={stockItemsPage === 1}
                        className={`px-3 py-1 border rounded text-sm disabled:opacity-50 ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'}`}
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setStockItemsPage(Math.min(totalStockItemsPages, stockItemsPage + 1))}
                        disabled={stockItemsPage === totalStockItemsPages}
                        className={`px-3 py-1 border rounded text-sm disabled:opacity-50 ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'}`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                  <div className={`mt-2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    Use arrow keys to navigate, Enter to select, Escape to close
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Details Modal */}
        {showDetailsModal && selectedEntry && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`rounded-lg shadow-xl w-11/12 md:w-4/5 lg:w-3/4 max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex justify-between items-center">
                  <h3 className={`text-lg font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                    Destroy Entry Details - {selectedEntry.destroyId}
                  </h3>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className={darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}
                  >
                    <FaTimes size={20} />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
                    <div className="flex items-center">
                      <FaCalendarAlt className="text-red-600 mr-2" />
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Destroy Date</p>
                        <p className={`font-semibold ${darkMode ? 'text-gray-100' : ''}`}>{formatDate(selectedEntry.destroyDate)}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                    <div className="flex items-center">
                      <FaBox className="text-blue-600 mr-2" />
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Items</p>
                        <p className={`font-semibold ${darkMode ? 'text-gray-100' : ''}`}>{selectedEntry.items?.length || 0}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
                    <div className="flex items-center">
                      <FaBoxes className="text-yellow-600 mr-2" />
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Damaged Qty</p>
                        <p className={`font-semibold ${darkMode ? 'text-gray-100' : ''}`}>
                          {selectedEntry.items?.reduce((sum, item) => sum + (item.destroyedDamagedQuantity || 0), 0) || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
                    <div className="flex items-center">
                      <FaFileAlt className="text-purple-600 mr-2" />
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Status</p>
                        <div className="mt-1">{getStatusBadge(selectedEntry.status)}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Entry Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Location</label>
                      <div className={`mt-1 p-3 rounded-md ${darkMode ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
                        <p className={`text-sm font-medium ${darkMode ? 'text-red-400' : 'text-red-800'}`}>
                          {selectedEntry.location?.name || selectedEntry.location?.locationName || 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Reason for Destruction</label>
                      <div className={`mt-1 p-3 rounded-md ${darkMode ? 'bg-yellow-900/20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'}`}>
                        <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedEntry.reason}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Created By</label>
                      <div className={`mt-1 p-3 rounded-md ${darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-gray-50 border border-gray-200'}`}>
                        <p className={`text-sm font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                          <FaUser className="inline mr-1" />
                          {selectedEntry.createdBy?.name || 'Unknown'}
                        </p>
                        {selectedEntry.createdBy?.email && (
                          <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{selectedEntry.createdBy.email}</p>
                        )}
                      </div>
                    </div>
                    {selectedEntry.approvedBy && (
                      <div>
                        <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {selectedEntry.status === 'approved' ? 'Approved' : 'Rejected'} By
                        </label>
                        <div className={`mt-1 p-3 rounded-md ${darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-gray-50 border border-gray-200'}`}>
                          <p className={`text-sm font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                            {selectedEntry.approvedBy?.name || 'Unknown'}
                          </p>
                          {selectedEntry.approvalDate && (
                            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              on {formatDate(selectedEntry.approvalDate)}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {selectedEntry.remarks && (
                  <div className="mb-6">
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Remarks</label>
                    <div className={`p-3 rounded-md ${darkMode ? 'bg-yellow-900/20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'}`}>
                      <p className={`text-sm whitespace-pre-wrap ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedEntry.remarks}</p>
                    </div>
                  </div>
                )}

                {selectedEntry.outwardEntryId && (
                  <div className="mb-6">
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Outward Entry Reference</label>
                    <div className={`p-3 rounded-md ${darkMode ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
                      <p className={`text-sm font-medium ${darkMode ? 'text-blue-400' : 'text-blue-800'}`}>
                        {selectedEntry.outwardEntryId?.outwardId || selectedEntry.outwardEntryId}
                      </p>
                    </div>
                  </div>
                )}

                {/* Items Table */}
                <div>
                  <label className={`block text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Destroyed Items</label>
                  <div className={`overflow-x-auto border rounded-lg ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                        <tr>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Stock Item
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Brand & Category
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Damaged Qty Destroyed
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Unit
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Reason
                          </th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                        {selectedEntry.items?.map((item, index) => (
                          <tr key={index} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                            <td className="px-4 py-4">
                              <div>
                                <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {item.stockItemId?.name || 'N/A'}
                                </p>
                                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  ID: {item.stockItemId?.id || 'N/A'}
                                </p>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="space-y-1">
                                <span className={`text-xs px-2 py-1 rounded ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                                  {item.stockItemId?.brand?.name || 'No Brand'}
                                </span>
                                <br />
                                <span className={`text-xs px-2 py-1 rounded ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-800'}`}>
                                  {item.stockItemId?.category?.name || 'No Category'}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                {item.destroyedDamagedQuantity || 0}
                              </span>
                            </td>
                            <td className={`px-4 py-4 text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {item.stockItemId?.unitOfMeasure || 'N/A'}
                            </td>
                            <td className={`px-4 py-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {item.reason || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Approval Actions */}
                {selectedEntry.status === 'pending' && (
                  <div className={`mt-6 p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h4 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Approval Actions</h4>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleApproval(selectedEntry._id, 'approved')}
                        disabled={approving}
                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                      >
                        <FaCheck className="mr-2" />
                        {approving ? 'Processing...' : 'Approve'}
                      </button>
                      <button
                        onClick={() => handleApproval(selectedEntry._id, 'rejected')}
                        disabled={approving}
                        className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                      >
                        <FaBan className="mr-2" />
                        {approving ? 'Processing...' : 'Reject'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockItemsDestroy;
